// A match's POSTER: the few kilobytes a list of matches needs to draw one match,
// so it never has to download the replay itself (up to ~270KB each; a profile
// lists dozens). Saved beside the players' copy of the replay when a match ends.
//
//   { v, map: { name, width, height, round, shape }, rounds: [{ winner, order }],
//     seats: [{ id, colorId, name, tag? }], trails: [{ c: colorId, l: [[x,y,x,y,...], ...] }] }
//
//   - trails: the LAST round's worm trails as coarse polylines in whole game units
//     (one polyline per unbroken stretch: a jump or a wall-hack wrap starts a new
//     one). Sampled about every map/50: a couple of pixels at thumbnail size. A full
//     25-player match comes to ~15KB (~6KB gzipped), a Duel to about 1KB.
//   - map.shape: the arena's outline, "rectangle" (squares too), "circle" or
//     "stadium" (a pill lying along x). Posters from before it carry only `round`
//     (true for a circle), which stays for them.
//   - rounds: who won each round, and every seat's finishing order in it, which is
//     what "you took 3 of 6" and a round's chip read.
//   - seats[].tag: a short hash of a signed-in seat's account id (seatTag below).
//     A client that knows an account id (its own profile's) can find that seat;
//     to anyone else it says nothing. The poster is as public as the replay.
import * as trajectoryUtil from "./geometry/trajectory/trajectory-util.js";
import * as scoreUtil from "./score/score-util.js";
import * as gsf from "./game-state-functions.js";

export var REPLAY_POSTER_VERSION = 1;
var SAMPLES_ACROSS_MAP = 50;

function trailOf(segments, step, playedUntil) {
    var lines = [], line = null, lastX = null, lastY = null;
    function close() { if (line && line.length >= 4) { lines.push(line); } line = null; lastX = null; }
    (segments || []).forEach(function (seg) {
        if (seg.startTime > playedUntil) { return; } // the victory lap is not part of the round's picture
        if (!(seg.speed > 0) || seg.jump || seg.type === "still_arc" || seg.type === "worm_died") {
            if (seg.jump) { close(); }
            return;
        }
        var duration = Math.min(seg.duration || 0, Math.max(0, playedUntil - seg.startTime));
        var length = seg.speed * duration;
        var n = Math.max(1, Math.ceil(length / step));
        for (var k = 0; k <= n; k++) {
            var p = trajectoryUtil.followTrajectory(seg, (k / n) * duration);
            var x = Math.round(p.x), y = Math.round(p.y);
            // A wrap (wall hack) or any other teleport: never draw a line across the arena.
            if (lastX !== null && Math.abs(x - lastX) + Math.abs(y - lastY) > step * 4) { close(); }
            if (!line) { line = []; }
            if (x !== lastX || y !== lastY) { line.push(x, y); lastX = x; lastY = y; }
        }
    });
    close();
    return lines;
}

// matchConfig: the players as clients see them. roundsData: match.matchState.roundsData
// with LIVE segments (not wire form). seatTags: { playerId: tag } for signed-in seats.
export function buildReplayPoster(options) {
    var matchConfig = options.matchConfig || {}, rounds = options.roundsData || [], seatTags = options.seatTags || {};
    var players = matchConfig.players || [];
    var map = matchConfig.map || {};
    var colorOf = {};
    players.forEach(function (p) { colorOf[p.id] = p.colorId; });

    var last = rounds.length ? rounds[rounds.length - 1].gameState : null;
    var trails = [];
    var shapeOf = (last && last.map && last.map.shape) || map.shape || null;
    var shape = shapeOf && (shapeOf.type === "circle" || shapeOf.type === "stadium") ? shapeOf.type : "rectangle";
    if (last) {
        var step = Math.max(4, Math.max(map.width || 0, map.height || 0) / SAMPLES_ACROSS_MAP);
        Object.keys(last.wormPathSegments || {}).forEach(function (trackId) {
            var segments = last.wormPathSegments[trackId];
            if (!segments || !segments.length) { return; }
            var lines = trailOf(segments, step, last.gameTime);
            if (lines.length) { trails.push({ c: colorOf[segments[0].playerId] || null, l: lines }); }
        });
    }
    return {
        v: REPLAY_POSTER_VERSION,
        map: { name: map.name || null, width: map.width || 0, height: map.height || 0, round: !!(last && gsf.isRoundMap(last)), shape: shape },
        rounds: rounds.map(function (r) {
            var order = scoreUtil.createSortedList(r.roundScore || {}).map(function (e) { return e.id; });
            return { winner: order[0] || null, order: order };
        }),
        seats: players.map(function (p) {
            var seat = { id: p.id, colorId: p.colorId, name: p.name };
            if (seatTags[p.id]) { seat.tag = seatTags[p.id]; }
            return seat;
        }),
        trails: trails
    };
}

/* global crypto, TextEncoder */
// The short, one-way tag of an account id (12 hex of its SHA-256). Async: it uses
// Web Crypto, which the Worker, the browser and Node all have.
export function seatTag(accountId) {
    var bytes = new TextEncoder().encode("achtung-seat:" + String(accountId));
    return crypto.subtle.digest("SHA-256", bytes).then(function (hash) {
        var out = "", view = new Uint8Array(hash);
        for (var i = 0; i < 6; i++) { out += (view[i] < 16 ? "0" : "") + view[i].toString(16); }
        return out;
    });
}
